import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ingredientsApi } from '@/api/ingredients';
import { useToast }       from '@/providers/ToastProvider';
import { useAuth }        from '@/providers/AuthProvider';
import {
  PageHeader, Button, FormField, Input, Card, CardContent,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  ConfirmDialog, Spinner,
} from '@/components/ui';
import { Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import type { IngredientCategory } from '@/types';

const schema = z.object({
  name: z.string().min(1, 'required').max(100),
});
type FormValues = z.infer<typeof schema>;

export default function CategoriesPage() {
  const { t }         = useTranslation();
  const queryClient   = useQueryClient();
  const { toast }     = useToast();
  const { isAdmin }   = useAuth();

  const [editing, setEditing]           = useState<IngredientCategory | null>(null);
  const [creating, setCreating]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IngredientCategory | null>(null);

  const formOpen = creating || !!editing;

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn:  ingredientsApi.listCategories,
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
  });

  const openCreate = () => {
    reset({ name: '' });
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (cat: IngredientCategory) => {
    reset({ name: cat.name });
    setEditing(cat);
    setCreating(false);
  };

  const closeForm = () => {
    setCreating(false);
    setEditing(null);
    reset({ name: '' });
  };

  const createMutation = useMutation({
    mutationFn: ({ name }: FormValues) => ingredientsApi.createCategory(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories-all'] });
      toast(t('categories.created'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ name }: FormValues) => ingredientsApi.updateCategory(editing!.id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories-all'] });
      toast(t('categories.updated'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingredientsApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['categories-all'] });
      toast(t('categories.deleted'), 'success');
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const onSubmit = (data: FormValues) => {
    if (editing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <PageHeader title={t('categories.title')} subtitle={t('categories.subtitle')}>
        {isAdmin && (
          <Button size="sm" onClick={openCreate} disabled={formOpen}>
            <Plus size={14} /> {t('categories.newCategory')}
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ── Side form ─────────────────────────────────────────────────── */}
        {formOpen && isAdmin && (
          <Card className="lg:col-span-1 order-0 lg:order-1">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">
                  {editing ? t('categories.editCategory') : t('categories.newCategory')}
                </h3>
                <Button variant="ghost" size="icon" onClick={closeForm}>
                  <X size={14} />
                </Button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-3">
                <FormField label={t('categories.name')} error={errors.name?.message} required>
                  <Input {...register('name')} autoFocus />
                </FormField>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={isSaving} loading={isSaving} className="flex-1">
                    <Check size={14} /> {isSaving ? t('common.saving') : t('common.save')}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <div className={formOpen ? 'lg:col-span-2' : 'lg:col-span-3'}>
          {isLoading ? (
            <Spinner className="py-16" />
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    {isAdmin && (
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-muted-foreground py-10">
                        {t('common.noResults')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    categories.map(cat => (
                      <TableRow key={cat.id}>
                        <TableCell className="font-medium">{cat.name}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost" size="icon"
                                title={t('common.edit')}
                                onClick={() => openEdit(cat)}
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="ghost" size="icon"
                                className="text-destructive hover:text-destructive"
                                title={t('common.delete')}
                                onClick={() => setDeleteTarget(cat)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('categories.deleteTitle')}
        description={t('categories.deleteDescription', { name: deleteTarget?.name })}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
